import { Box, Typography } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useFormatDuration } from "hooks/useFormatDuration";
import { useFormatNumber } from "hooks/useFormatNumber";
import { NightReport } from "interfaces/nightReport.interface";

import { MetricCell } from "./MetricCell";
import { NightReportFieldCell } from "./NightReportFieldCell";

const isBaselineInsufficient = (count?: number | null) =>
  count !== null && count !== undefined && count <= 2;

export type NightReportFieldType =
  | "SUBJECT_ID"
  | "RESPIRATION_RATE"
  | "MOVEMENT"
  | "TIME_ASLEEP"
  | "SLEEP_SCORE"
  | "OUT_OF_BED"
  | "SESSION_IS_IN_PROGRESS"
  | "BASELINE_NIGHT_COUNT"
  | "EMPTY"
  | "REPORT_LINK";

type NightReportFieldProps = {
  type: NightReportFieldType;
  report: NightReport;
};

export function NightReportField({ type, report }: NightReportFieldProps) {
  const { t } = useTranslation();
  const formatNumber = useFormatNumber();
  const formatDuration = useFormatDuration();

  switch (type) {
    case "EMPTY":
      return <NightReportFieldCell testId="empty" />;
    case "REPORT_LINK":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            color: "texts.neutral.subtle",
          }}
        >
          <Link
            key={report.subject?.id}
            to={`/subject/${report.subject?.id}/sessions?sessionId=${report.session_id}`}
            style={{
              color: "inherit",
              textDecoration: "underline",
            }}
          >
            <Typography variant="bodySmallRegular">
              Go to sleep report
            </Typography>
          </Link>
        </Box>
      );
    case "SUBJECT_ID":
      return (
        <NightReportFieldCell
          cellText={report.subject?.identifier || ""}
          testId={"subject_id"}
          title={t("subject")}
          toolTip={t("night_report_subject_info")}
        />
      );
    case "RESPIRATION_RATE":
      return (
        <MetricCell
          metric={report.respiration_rate}
          title={t("Respiration Rate (rpm)")}
          toolTip={t("night_report_respiration_rate_info")}
          belowThreshold={isBaselineInsufficient(report.baseline_night_count)}
          alertMetric="respiration_rate"
          valueFormatter={(n) => formatNumber(n, { decimals: 1 })}
          deltaSource="deviation_pct"
          showPercent
          belowMissingFallback=""
          testId="respiration_rate"
        />
      );
    case "MOVEMENT":
      return (
        <MetricCell
          metric={report.movement}
          title={t("Movement")}
          toolTip={t("night_report_movement_info")}
          belowThreshold={isBaselineInsufficient(report.baseline_night_count)}
          alertMetric="movement"
          valueFormatter={(n) => formatNumber(n)}
          deltaSource="deviation_pct"
          showPercent
          belowMissingFallback=""
          testId="movement"
        />
      );
    case "TIME_ASLEEP":
      return (
        <MetricCell
          metric={report.time_asleep}
          title={t("time_asleep")}
          toolTip={t("night_report_time_asleep_info")}
          belowThreshold={isBaselineInsufficient(report.baseline_night_count)}
          alertMetric="time_asleep"
          valueFormatter={(n) => formatDuration(n)}
          deltaSource="deviation_pct"
          showPercent
          testId="time_asleep"
        />
      );
    case "SLEEP_SCORE":
      return (
        <MetricCell
          metric={
            report.sleep_score &&
            report.sleep_score.value !== null &&
            report.sleep_score.value >= 0
              ? report.sleep_score
              : null
          }
          title={t("Sleep Score")}
          toolTip={t("night_report_sleep_score_info")}
          belowThreshold={isBaselineInsufficient(report.baseline_night_count)}
          valueFormatter={(n) => formatNumber(n)}
          deltaSource="deviation_pct"
          showPercent={true}
          belowMissingFallback=""
          testId="sleep_score"
        />
      );
    case "OUT_OF_BED":
      return (
        <MetricCell
          metric={report.out_of_bed}
          title={t("Out of Bed")}
          toolTip={t("night_report_out_of_bed_info")}
          belowThreshold={isBaselineInsufficient(report.baseline_night_count)}
          valueFormatter={(n) => formatNumber(n)}
          deltaSource="baseline"
          showPercent={false}
          testId="out_of_bed"
        />
      );
    case "SESSION_IS_IN_PROGRESS":
      return (
        <NightReportFieldCell
          cellText={report.selected_session_is_in_progress ? t("Yes") : t("No")}
          testId={"subject_id"}
          title={t("Session in Progress")}
          toolTip={t("night_report_selected_session_is_in_progress_info")}
        />
      );
    case "BASELINE_NIGHT_COUNT": {
      const baseline = report.baseline_night_count;
      return (
        <NightReportFieldCell
          cellText={baseline || t("N/A")}
          testId={"subject_id"}
          title={t("Baseline Nights")}
          toolTip={t("night_report_baseline_night_count_info")}
        />
      );
    }
  }
}
